import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { getVNodeDetails } from "../../../http/http-calls";
import { socketConnect } from "socket.io-react";
import { connect } from "react-redux";
import { hideLoader, showLoader } from "../../../redux/actions/loader-data";
import { makeGetRequest } from "../../../http/http-service";
import config from "../../../config/index";


const DockerTerminalModal = ({ open, toggle, uuid, id, isStandalone = false}) => {
    const terminalRef = useRef(null);
    const socketRef = useRef(null);
    const fitAddonRef = useRef(null);
    const terminalElementRef = useRef(null);
    const resizeListenerRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    const [containerInfo, setContainerInfo] = useState({
        uuid: null,
        name: "",
        status: null,
        loading: false,
        error: null,
    });

    const [connectionState, setConnectionState] = useState({
        isConnected: false,
        isEnded: false,
        canReconnect: false,
    });

    // File upload functionality
    const [fileUploadState, setFileUploadState] = useState({
        isUploading: false,
        uploadProgress: 0,
        uploadMessage: "",
        showUploadForm: false,
        containerPath: "/tmp"
    });

    // Close upload form when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const uploadButton = document.querySelector('.file-upload-button');
            const uploadForm = document.querySelector('.file-upload-form');
            
            if (uploadButton && uploadForm &&
                !uploadButton.contains(event.target) &&
                !uploadForm.contains(event.target)) {
                setFileUploadState(prev => ({ ...prev, showUploadForm: false }));
            }
        };

        if (fileUploadState.showUploadForm) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [fileUploadState.showUploadForm]);

    const initializeWebSocket = useCallback(() => {
        console.log('=== INITIALIZING WEBSOCKET CONNECTION ===');
        console.log('Container UUID for WebSocket:', containerInfo.uuid);

        // Skip container status check since it's causing 401 errors
        // The WebSocket backend will handle container status checking
        console.log('Skipping container status check - WebSocket backend will handle it');

        const wsUrl = new URL('wss://c3.gurooevos.com/api/v1/terminals/docker/');
        wsUrl.searchParams.set('uuid', containerInfo.uuid);

        console.log('=== WEBSOCKET CONNECTION DETAILS ===');
        console.log('WebSocket URL:', wsUrl.toString());

        // Check if we have any indication that the container might not be running
        // If we don't have explicit status, we'll still attempt the connection
        // but log that we're proceeding without status information
        if (containerInfo.status && (containerInfo.status === 'exited' || containerInfo.status === 'stopped' || containerInfo.status === 'created')) {
            console.log(`❌ Container ${containerInfo.uuid} is not running (Status: ${containerInfo.status}), skipping WebSocket connection`);
            if (terminalRef.current) {
                terminalRef.current.write(`\r\n❌ Container is not running (Status: ${containerInfo.status})\r\n`);
                terminalRef.current.write('Please start the container first before connecting to terminal.\r\n');
            }
            setConnectionState({ isConnected: false, isEnded: true, canReconnect: false });
            return;
        } else if (!containerInfo.status) {
            console.log(`⚠️ No container status available for ${containerInfo.uuid}, attempting WebSocket connection`);
        }

        const socket = new WebSocket(wsUrl.toString());
        socketRef.current = socket;

        // Track connection state
        let connectionTimeout;
        const connectionTimeoutDuration = 10000;

        socket.onopen = () => {
            clearTimeout(connectionTimeout);
            console.log('=== WEBSOCKET CONNECTION OPENED ===');
            setConnectionState({ isConnected: true, isEnded: false, canReconnect: false });
            terminalRef.current?.write('\r\nConnected to container terminal\r\n');

            // Send initial terminal dimensions with proper error handling
            try {
                if (fitAddonRef.current && terminalRef.current) {
                    const dimensions = fitAddonRef.current.proposeDimensions();
                    if (dimensions && dimensions.cols > 0 && dimensions.rows > 0) {
                        socket.send(JSON.stringify({type: 'resize', cols: dimensions.cols, rows: dimensions.rows}));
                    } else {
                        // Fallback to default dimensions
                        socket.send(JSON.stringify({type: 'resize', cols: 80, rows: 24}));
                    }
                } else {
                    // FitAddon not ready, use default dimensions
                    socket.send(JSON.stringify({type: 'resize', cols: 80, rows: 24}));
                }
            } catch (err) {
                console.warn('Error getting terminal dimensions:', err);
                // Send default dimensions as fallback
                socket.send(JSON.stringify({type: 'resize', cols: 80, rows: 24 }));
            }
        };

        socket.onmessage = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                terminalRef.current?.write(data.output || data);
            } catch (err) {
                console.warn('Error processing WebSocket message:', err);
                terminalRef.current?.write('\r\nReceived malformed terminal data\r\n');
            }
        };

        socket.onerror = (error) => {
            clearTimeout(connectionTimeout);
            console.error('WebSocket error:', {event: error, readyState: socket.readyState, url: wsUrl.toString(), timestamp: new Date().toISOString()});

            let errorMessage = 'Connection error';

            // More detailed error analysis
            if (socket.readyState === WebSocket.CLOSED) {
                errorMessage = 'Connection closed unexpectedly';
            } else if (!navigator.onLine) {
                errorMessage = 'Network connection lost';
            }

            terminalRef.current?.write(`\r\n${errorMessage}\r\n`);

            // Attempt reconnection if appropriate
            if (socket.readyState !== WebSocket.OPEN && reconnectAttemptsRef.current < 3) {
                reconnectAttemptsRef.current++;
                setTimeout(() => {
                    terminalRef.current?.write(`\r\nAttempting to reconnect (${reconnectAttemptsRef.current}/3)...\r\n`);
                    initializeWebSocket();
                }, 3000);
            } else if (reconnectAttemptsRef.current >= 3) {
                terminalRef.current?.write(`\r\nMaximum reconnection attempts reached.\r\n`);
            }
        };

        socket.onclose = (event) => {
            clearTimeout(connectionTimeout);
            console.log('WebSocket closed:', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                timestamp: new Date().toISOString()
            });

            let closeMessage = 'Connection closed';
            let canReconnect = false;
            
            switch (event.code) {
                case 1000:
                    closeMessage = 'Bash session ended';
                    canReconnect = true;
                    break;
                case 1008:
                    closeMessage = `Authentication failed: ${event.reason || 'Invalid token or permissions'}`;
                    break;
                case 1006:
                    closeMessage = 'Connection failed (check your network)';
                    canReconnect = true;
                    break;
                case 1011:
                    closeMessage = 'Server error occurred';
                    canReconnect = true;
                    break;
                case 4000:
                    closeMessage = 'Container UUID missing - invalid request';
                    break;
                case 4001:
                    closeMessage = 'Container is not running - please start the Docker first';
                    break;
                case 4002:
                    closeMessage = 'Failed to start shell session - container may be misconfigured';
                    break;
                default:
                    closeMessage = 'Connection closed normally';
                    canReconnect = true;
            }

            setConnectionState({ isConnected: false, isEnded: true, canReconnect });
            
            terminalRef.current?.write(`\r\n${closeMessage}\r\n`);
            
            if (canReconnect) {
                terminalRef.current?.write('\r\nPress "Reconnect" button to start a new bash session\r\n');
            }
        };

        // Set connection timeout
        connectionTimeout = setTimeout(() => {
            if (socket.readyState === WebSocket.CONNECTING) {
                console.warn('WebSocket connection timeout');
                socket.close();
                terminalRef.current?.write('\r\nConnection timed out\r\n');
            }
        }, connectionTimeoutDuration);

        return () => {
            clearTimeout(connectionTimeout);
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }
        };
    }, [containerInfo.uuid]);

    // Cleanup function for terminal and WebSocket
    const cleanupTerminal = useCallback(() => {
        // Remove resize event listener
        if (resizeListenerRef.current) {
            window.removeEventListener('resize', resizeListenerRef.current);
            resizeListenerRef.current = null;
        }

        // Remove clipboard event listeners
        if (terminalElementRef.current) {
            if (terminalElementRef.current._clipboardKeyHandler) {
                terminalElementRef.current.removeEventListener('keydown', terminalElementRef.current._clipboardKeyHandler);
                terminalElementRef.current._clipboardKeyHandler = null;
            }
            if (terminalElementRef.current._clipboardContextHandler) {
                terminalElementRef.current.removeEventListener('contextmenu', terminalElementRef.current._clipboardContextHandler);
                terminalElementRef.current._clipboardContextHandler = null;
            }
        }

        // Close WebSocket
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        // Dispose terminal
        if (terminalRef.current) {
            terminalRef.current.dispose();
            terminalRef.current = null;
        }

        // Clean up fit addon reference
        fitAddonRef.current = null;
    }, []);

    // Initialize WebSocket connection to the server

    // Initialize terminal and setup handlers
    const initializeTerminal = useCallback(() => {
        if (!terminalElementRef.current || !containerInfo.uuid) {
            console.debug('Missing terminal element or container UUID');
            return;
        }

        // Cleanup previous terminal if exists
        cleanupTerminal();

        // Create terminal with Canvas2D performance optimizations
        const term = new Terminal({
            cursorBlink: true,
            theme: {
                background: "#1e1e1e",
                foreground: "#ffffff",
            },
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            fontSize: 14,
            rows: 24,
            cols: 80,
            allowTransparency: true,
            rendererType: 'canvas', 
            convertEol: true,
            allowProposedApi: true,
            disableStdin: false,
            rightClickSelectsWord: true,
            rendererConfig: {
                gpu: true,
                canvasWillReadFrequently: true
            }
        });

        // Store reference immediately
        terminalRef.current = term;

        // Use a more reliable method to ensure DOM is ready before opening terminal
        const prepareAndOpenTerminal = () => {
            try {
                // Only proceed if the terminal element is available and has dimensions
                if (!terminalElementRef.current) {
                    console.warn("Terminal element not available");
                    setTimeout(prepareAndOpenTerminal, 100);
                    return;
                }
                
                // Check if the element has been properly laid out
                if (terminalElementRef.current.offsetWidth <= 0 || 
                    terminalElementRef.current.offsetHeight <= 0) {
                    console.debug("Terminal element has no dimensions yet, waiting...");
                    setTimeout(prepareAndOpenTerminal, 100);
                    return;
                }
                
                // Open terminal in the container element
                term.open(terminalElementRef.current);
                
                // Start with a welcome message
                term.write("\r\nInitializing terminal...\r\n");
                
                // Initialize fit addon only after terminal is open
                let fitAddon;
                try {
                    fitAddon = new FitAddon();
                    term.loadAddon(fitAddon);
                    fitAddonRef.current = fitAddon;                    
                    term.resize(80, 24);
                } catch (fitErr) {
                    term.write("\r\nWarning: Terminal resize functionality may be limited\r\n");
                }

                term.onData(data => {
                    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                        socketRef.current.send(JSON.stringify({
                            type: 'input',
                            data: data
                        }));
                    }
                });

                // Add clipboard support
                const fallbackCopyText = (text) => {
                    try {
                        // Create a temporary textarea element
                        const textArea = document.createElement('textarea');
                        textArea.value = text;
                        textArea.style.position = 'fixed';
                        textArea.style.left = '-999999px';
                        textArea.style.top = '-999999px';
                        document.body.appendChild(textArea);
                        textArea.focus();
                        textArea.select();
                        
                        // Try to copy using the legacy execCommand
                        const successful = document.execCommand('copy');
                        document.body.removeChild(textArea);
                        
                        if (successful) {
                            console.log('Text copied using fallback method:', text);
                            term.write('\r\n✓ Copied to clipboard (legacy method)\r\n');
                        } else {
                            throw new Error('execCommand failed');
                        }
                    } catch (err) {
                        console.warn('Fallback copy method failed:', err);
                        term.write('\r\n✗ Copy failed - clipboard not supported\r\n');
                    }
                };

                const addClipboardSupport = () => {
                    const terminalElement = terminalElementRef.current;
                    if (!terminalElement) return;

                    // Add keyboard shortcuts for copy/paste
                    const handleKeyDown = (e) => {
                        // Only handle Ctrl+Shift+C for copy (when text is selected)
                        if (e.ctrlKey && e.shiftKey && e.key === 'C' && term.hasSelection()) {
                            e.preventDefault();
                            e.stopPropagation();
                            const selection = term.getSelection();
                            if (selection) {
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(selection).then(() => {
                                        console.log('Text copied to clipboard:', selection);
                                    }).catch(err => {
                                        console.warn('Failed to copy to clipboard:', err);
                                        fallbackCopyText(selection);
                                    });
                                } else {
                                    fallbackCopyText(selection);
                                }
                            }
                            return;
                        }

                        // Ctrl+Shift+V for paste
                        if (e.ctrlKey && e.shiftKey && e.key === 'V') {
                            e.preventDefault();
                            e.stopPropagation();
                            navigator.clipboard.readText().then(text => {
                                if (text && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                                    socketRef.current.send(JSON.stringify({
                                        type: 'input',
                                        data: text
                                    }));
                                }
                            }).catch(err => {
                                console.warn('Failed to read from clipboard:', err);
                                term.write('\r\n✗ Paste failed\r\n');
                            });
                            return;
                        }
                    };

                    // Add right-click context menu for copy/paste
                    const handleContextMenu = (e) => {
                        e.preventDefault();
                        
                        // Create context menu
                        const contextMenu = document.createElement('div');
                        contextMenu.style.position = 'fixed';
                        contextMenu.style.left = e.clientX + 'px';
                        contextMenu.style.top = e.clientY + 'px';
                        contextMenu.style.backgroundColor = '#2d2d2d';
                        contextMenu.style.border = '1px solid #444';
                        contextMenu.style.borderRadius = '4px';
                        contextMenu.style.padding = '5px 0';
                        contextMenu.style.zIndex = '10000';
                        contextMenu.style.minWidth = '120px';
                        contextMenu.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';

                        // Copy option (only if text is selected)
                        if (term.hasSelection()) {
                            const copyOption = document.createElement('div');
                            copyOption.textContent = 'Copy';
                            copyOption.style.padding = '8px 12px';
                            copyOption.style.color = '#fff';
                            copyOption.style.cursor = 'pointer';
                            copyOption.style.fontSize = '13px';
                            copyOption.onmouseover = () => copyOption.style.backgroundColor = '#404040';
                            copyOption.onmouseout = () => copyOption.style.backgroundColor = 'transparent';
                            copyOption.onclick = () => {
                                const selection = term.getSelection();
                                if (selection) {
                                    // Try modern Clipboard API first
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(selection).then(() => {
                                            console.log('Text copied to clipboard:', selection);
                                            term.write('\r\n✓ Copied to clipboard\r\n');
                                        }).catch(err => {
                                            console.warn('Failed to copy to clipboard:', err);
                                            // Fallback to legacy method
                                            fallbackCopyText(selection);
                                        });
                                    } else {
                                        // Use legacy method
                                        fallbackCopyText(selection);
                                    }
                                }
                                document.body.removeChild(contextMenu);
                            };
                            contextMenu.appendChild(copyOption);
                        }

                        // Paste option
                        const pasteOption = document.createElement('div');
                        pasteOption.textContent = 'Paste';
                        pasteOption.style.padding = '8px 12px';
                        pasteOption.style.color = '#fff';
                        pasteOption.style.cursor = 'pointer';
                        pasteOption.style.fontSize = '13px';
                        pasteOption.onmouseover = () => pasteOption.style.backgroundColor = '#404040';
                        pasteOption.onmouseout = () => pasteOption.style.backgroundColor = 'transparent';
                        pasteOption.onclick = () => {
                            navigator.clipboard.readText().then(text => {
                                if (text && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                                    socketRef.current.send(JSON.stringify({
                                        type: 'input',
                                        data: text
                                    }));
                                }
                            }).catch(err => {
                                console.warn('Failed to read from clipboard:', err);
                                term.write('\r\n✗ Paste failed\r\n');
                            });
                            document.body.removeChild(contextMenu);
                        };
                        contextMenu.appendChild(pasteOption);

                        // Add to body
                        document.body.appendChild(contextMenu);

                        // Remove context menu when clicking elsewhere
                        const removeContextMenu = (event) => {
                            if (!contextMenu.contains(event.target)) {
                                if (document.body.contains(contextMenu)) {
                                    document.body.removeChild(contextMenu);
                                }
                                document.removeEventListener('click', removeContextMenu);
                            }
                        };
                        setTimeout(() => document.addEventListener('click', removeContextMenu), 0);
                    };

                    // Attach event listeners to the terminal element
                    terminalElement.addEventListener('keydown', handleKeyDown, true); // Use capture phase
                    terminalElement.addEventListener('contextmenu', handleContextMenu);
                    
                    // Store references for cleanup
                    terminalElement._clipboardKeyHandler = handleKeyDown;
                    terminalElement._clipboardContextHandler = handleContextMenu;
                };

                // Add clipboard support after terminal is opened
                addClipboardSupport();

                // Check clipboard permissions and show appropriate message
                if (navigator.clipboard && navigator.clipboard.readText) {
                    navigator.permissions.query({name: "clipboard-read"}).then(result => {
                        if (result.state === "granted" || result.state === "prompt") {
                            console.log("Clipboard access available");
                        } else {
                            term.write("⚠️  Clipboard access may be limited. Enable clipboard permissions in browser settings.\r\n");
                        }
                    }).catch(() => {
                        console.log("Clipboard permission query not supported, but clipboard API is available");
                    });
                } else {
                    term.write("⚠️  Clipboard API not supported in this browser. Copy/paste may not work.\r\n");
                }

                // Create a debounced resize handler to avoid too many resize events
                let resizeTimeout;
                const handleResize = () => {
                    if (resizeTimeout) {
                        clearTimeout(resizeTimeout);
                    }
                    
                    // Set new timeout
                    resizeTimeout = setTimeout(() => {
                        try {
                            if (!fitAddonRef.current || !terminalRef.current) return;
                            
                            // Check if the terminal element still has dimensions
                            if (terminalElementRef.current && 
                                terminalElementRef.current.offsetWidth > 0 && 
                                terminalElementRef.current.offsetHeight > 0) {
                                
                                // Perform the fit operation
                                fitAddonRef.current.fit();
                                console.debug('Terminal resized');
                                
                                // Send new dimensions to server if connection is open
                                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                                    try {
                                        const dimensions = fitAddonRef.current.proposeDimensions();
                                        if (dimensions && dimensions.cols > 0 && dimensions.rows > 0) {
                                            socketRef.current.send(JSON.stringify({
                                                type: 'resize',
                                                cols: dimensions.cols,
                                                rows: dimensions.rows
                                            }));
                                        }
                                    } catch (dimErr) {
                                        console.warn("Failed to get dimensions during resize:", dimErr);
                                    }
                                }
                            }
                        } catch (err) {
                            console.warn("Terminal resize error:", err);
                        }
                    }, 100); // Debounce for 100ms
                };

                // Add resize listener and store reference for cleanup
                window.addEventListener('resize', handleResize);
                resizeListenerRef.current = handleResize;

                // Apply initial fit with proper safeguards
                const performInitialFit = () => {
                    if (!fitAddonRef.current || !terminalRef.current) {
                        console.debug('FitAddon or terminal not ready for initial fit');
                        return;
                    }
                    
                    try {
                        // Verify terminal element has dimensions
                        if (terminalElementRef.current && 
                            terminalElementRef.current.offsetWidth > 0 && 
                            terminalElementRef.current.offsetHeight > 0) {
                            
                            console.debug('Performing initial terminal fit');
                            fitAddonRef.current.fit();
                            
                            // Also directly set dimensions on the terminal object with error handling
                            try {
                                const dimensions = fitAddonRef.current.proposeDimensions();
                                if (dimensions && dimensions.cols > 0 && dimensions.rows > 0) {
                                    terminalRef.current.resize(dimensions.cols, dimensions.rows);
                                    console.debug('Initial fit completed with dimensions:', dimensions);
                                } else {
                                    console.warn('Invalid dimensions from proposeDimensions:', dimensions);
                                    // Use fallback dimensions
                                    terminalRef.current.resize(80, 24);
                                }
                            } catch (dimErr) {
                                console.warn('Error getting dimensions during initial fit:', dimErr);
                                // Use fallback dimensions
                                terminalRef.current.resize(80, 24);
                            }
                        } else {
                            console.warn("Terminal element not ready for initial fit");
                            // Try again shortly
                            setTimeout(performInitialFit, 50);
                        }
                    } catch (err) {
                        console.warn("Initial fit error:", err);
                        // Try to set fallback dimensions directly on terminal
                        if (terminalRef.current) {
                            try {
                                terminalRef.current.resize(80, 24);
                            } catch (fallbackErr) {
                                console.error("Even fallback resize failed:", fallbackErr);
                            }
                        }
                    }
                };

                // Wait a bit longer for modal transition to complete before fitting
                setTimeout(performInitialFit, 200);

                // Initialize WebSocket connection after terminal setup
                initializeWebSocket();
                
            } catch (err) {
                console.error("Terminal initialization error:", err.toString());
                if (terminalRef.current) {
                    terminalRef.current.write(`\r\nTerminal initialization error: ${err.message}\r\n`);
                }
            }
        };
        
        // Start the initialization process
        setTimeout(prepareAndOpenTerminal, 100);
        
    }, [containerInfo.uuid, cleanupTerminal, initializeWebSocket]);

    // Effect to handle container info and initialize terminal
    useEffect(() => {
        if (!open) {
            cleanupTerminal();
            return;
        }

        if (!uuid) {
            setContainerInfo({
                uuid: null,
                name: "",
                loading: false,
                error: "Missing container UUID"
            });
            return;
        }

        console.log('=== DOCKER TERMINAL MODAL DEBUG ===');
        console.log('Received UUID from props:', uuid);
        console.log('Container ID from props:', id);
        
        setContainerInfo({
            uuid,
            name: "",
            status: null,
            loading: true,
            error: null
        });

        // Get container details
        if (id) {
            getVNodeDetails(id)
                .then((response) => {
                    setContainerInfo(prev => ({
                        ...prev,
                        uuid,
                        name: response.vNode?.name || response.vNode?.uuid || "Unknown",
                        status: response.vNode?.status || null,
                        loading: false
                    }));
                    
                    // Try to get container status from Docker directly
                    if (uuid) {
                        checkContainerStatus(uuid);
                    }
                })
                .catch((error) => {
                    console.error("Failed to get container details:", error);
                    setContainerInfo(prev => ({
                        ...prev,
                        loading: false,
                        error: error.message || "Failed to get container information"
                    }));
                });
        } else {
            setContainerInfo(prev => ({...prev, loading: false}));
        }

        return cleanupTerminal;
    }, [open, uuid, id, cleanupTerminal]);

    // Effect to initialize terminal when container info is set
    useEffect(() => {
        if (open && containerInfo.uuid && !containerInfo.loading && containerInfo.status !== undefined) {
            initializeTerminal();
        }
    }, [open, containerInfo.uuid, containerInfo.loading, containerInfo.status, initializeTerminal]);

    // Reconnect function to start a new bash session
    const reconnectToContainer = useCallback(() => {
        if (!containerInfo.uuid) return;
        
        console.log('=== RECONNECTING TO CONTAINER ===');
        
        // Clear the terminal and show reconnecting message
        if (terminalRef.current) {
            terminalRef.current.clear();
            terminalRef.current.write('\r\nReconnecting to container...\r\n');
        }
        
        // Reset connection state
        setConnectionState({ isConnected: false, isEnded: false, canReconnect: false });
        reconnectAttemptsRef.current = 0;
        
        // Close existing connection if any
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        
        // Start new connection
        setTimeout(() => {initializeWebSocket();}, 500);
    }, [containerInfo.uuid, initializeWebSocket]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !containerInfo.uuid) {
            setFileUploadState(prev => ({
                ...prev,
                uploadMessage: "No file selected or container not connected"
            }));
            return;
        }

        setFileUploadState(prev => ({
            ...prev,
            isUploading: true,
            uploadProgress: 0,
            uploadMessage: "Uploading file..."
        }));

        const formData = new FormData();
        formData.append("file", file);
        formData.append("container_id", containerInfo.uuid);
        formData.append("container_path", fileUploadState.containerPath);
        formData.append("file_name", file.name);

        try {
            const response = await fetch('/ronin/upload-file-to-container/', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (response.ok && result.status === "success") {
                setFileUploadState(prev => ({
                    ...prev,
                    uploadProgress: 100,
                    uploadMessage: `File uploaded successfully to ${result.file_path}`,
                    isUploading: false
                }));

                // Show success message in terminal
                if (terminalRef.current) {
                    terminalRef.current.write(`\r\n✅ File uploaded: ${file.name} -> ${result.file_path}\r\n`);
                }
            } else {
                throw new Error(result.message || "Upload failed");
            }
        } catch (error) {
            console.error('File upload error:', error);
            setFileUploadState(prev => ({
                ...prev,
                uploadMessage: `Upload failed: ${error.message}`,
                isUploading: false
            }));

            // Show error message in terminal
            if (terminalRef.current) {
                terminalRef.current.write(`\r\n❌ Upload failed: ${error.message}\r\n`);
            }
        }

        // Clear file input safely
        if (event.target && event.target.value) {
            event.target.value = '';
        }
    };

    // Check container status directly from Docker (placeholder for future implementation)
    // Currently, we don't have a direct container status API endpoint
    const checkContainerStatus = async (containerUuid) => {
        console.log(`Would check container status for ${containerUuid} if API endpoint existed`);
        // This is a placeholder for when we have a container status API endpoint
        // For now, we rely on the WebSocket backend to reject connections to non-running containers
    };

    // Render content based on mode
    const renderTerminalContent = () => (
        <>
            {/* Action buttons */}
            <div style={{
                marginBottom: "10px",
                display: "flex",
                gap: "10px",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {connectionState.canReconnect && (
                        <button
                            onClick={reconnectToContainer}
                            className="btn btn-success btn-sm"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                        >
                            🔄 Reconnect
                        </button>
                    )}
                    
                    {/* File Upload Button */}
                    <div style={{ position: "relative" }}>
                        <button
                            onClick={() => setFileUploadState(prev => ({
                                ...prev,
                                showUploadForm: !prev.showUploadForm,
                                uploadMessage: !prev.showUploadForm ? "" : prev.uploadMessage
                            }))}
                            className="btn btn-primary btn-sm"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                            disabled={!connectionState.isConnected}
                        >
                            📁 Upload File
                        </button>
                        
                        {fileUploadState.showUploadForm && (
                            <div style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                backgroundColor: "#2d2d2d",
                                border: "1px solid #444",
                                borderRadius: "4px",
                                padding: "10px",
                                zIndex: 1000,
                                minWidth: "250px",
                                boxShadow: "0 4px 8px rgba(0,0,0,0.3)"
                            }}>
                                <div style={{ marginBottom: "8px" }}>
                                    <label style={{ fontSize: "12px", color: "#fff", display: "block", marginBottom: "4px" }}>
                                        Upload Path:
                                    </label>
                                    <input
                                        type="text"
                                        value={fileUploadState.containerPath}
                                        onChange={(e) => setFileUploadState(prev => ({ ...prev, containerPath: e.target.value }))}
                                        style={{
                                            width: "100%",
                                            padding: "4px",
                                            fontSize: "12px",
                                            backgroundColor: "#1e1e1e",
                                            border: "1px solid #444",
                                            color: "#fff",
                                            borderRadius: "2px"
                                        }}
                                        placeholder="/tmp"
                                    />
                                </div>
                                <input
                                    type="file"
                                    id="file-upload-input"
                                    onChange={handleFileUpload}
                                    style={{ display: "none" }}
                                />
                                <button
                                    onClick={() => document.getElementById('file-upload-input').click()}
                                    className="btn btn-info btn-sm"
                                    style={{
                                        fontSize: "12px",
                                        padding: "4px 8px",
                                        width: "100%",
                                        marginBottom: "4px"
                                    }}
                                    disabled={fileUploadState.isUploading || !connectionState.isConnected}
                                >
                                    {fileUploadState.isUploading ? "📤 Uploading..." : "📁 Select File"}
                                </button>
                                <button
                                    onClick={() => setFileUploadState(prev => ({ ...prev, showUploadForm: false }))}
                                    className="btn btn-secondary btn-sm"
                                    style={{
                                        fontSize: "12px",
                                        padding: "4px 8px",
                                        width: "100%",
                                        marginBottom: "4px"
                                    }}
                                    disabled={fileUploadState.isUploading}
                                >
                                    Cancel
                                </button>
                                {fileUploadState.uploadMessage && (
                                    <div style={{
                                        fontSize: "11px",
                                        color: fileUploadState.uploadMessage.includes("success") ? "#28a745" : "#dc3545",
                                        marginTop: "4px"
                                    }}>
                                        {fileUploadState.uploadMessage}
                                    </div>
                                )}
                                {fileUploadState.isUploading && (
                                    <div style={{
                                        width: "100%",
                                        height: "4px",
                                        backgroundColor: "#444",
                                        borderRadius: "2px",
                                        marginTop: "4px",
                                        overflow: "hidden"
                                    }}>
                                        <div style={{
                                            width: `${fileUploadState.uploadProgress}%`,
                                            height: "100%",
                                            backgroundColor: "#007bff",
                                            transition: "width 0.3s ease"
                                        }} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    Status: {connectionState.isConnected ? (
                        <span style={{ color: "#28a745" }}>Connected</span>
                    ) : connectionState.isEnded ? (
                        <span style={{ color: "#ffc107" }}>Session Ended</span>
                    ) : (
                        <span style={{ color: "#6c757d" }}>Disconnected</span>
                    )}
                </div>
            </div>

            <div
                ref={terminalElementRef}
                id="docker-terminal"
                style={{
                    height: isStandalone ? "calc(100vh - 140px)" : "400px",
                    backgroundColor: "#1e1e1e",
                    padding: "10px",
                    borderRadius: "4px",
                }}
            />
        </>
    );

    // Modal mode - render with Modal wrapper
    return (
        <Modal
            isOpen={open}
            toggle={toggle}
            size="lg"
            className="docker-terminal-modal"
            onOpened={() => {
                const attemptFit = (attempt = 1, maxAttempts = 5) => {
                    if (attempt > maxAttempts) return;
                    if (!fitAddonRef.current || !terminalRef.current || !terminalElementRef.current) {
                        setTimeout(() => attemptFit(attempt + 1), 100);
                        return;
                    }
                    try {
                        if (terminalElementRef.current.offsetWidth > 0 && 
                            terminalElementRef.current.offsetHeight > 0) {
                            fitAddonRef.current.fit();
                            const dims = fitAddonRef.current.proposeDimensions();
                            if (dims && dims.cols > 0 && dims.rows > 0) {
                                terminalRef.current.resize(dims.cols, dims.rows);
                                if (socketRef.current?.readyState === WebSocket.OPEN) {
                                    socketRef.current.send(JSON.stringify({
                                        type: 'resize',
                                        cols: dims.cols,
                                        rows: dims.rows
                                    }));
                                }
                                return;
                            }
                        }
                    } catch (err) {}
                    setTimeout(() => attemptFit(attempt + 1), 100);
                };
                setTimeout(() => attemptFit(1), 300);
            }}
        >
            <ModalHeader toggle={toggle}>
                Docker Container Shell -{" "}
                {containerInfo.error
                    ? `Error: ${containerInfo.error}`
                    : containerInfo.loading
                      ? "Loading..."
                      : `${containerInfo.name || containerInfo.uuid || "Not Connected"}${containerInfo.status ? ` (${containerInfo.status})` : ''}`}
            </ModalHeader>
            <ModalBody>
                {renderTerminalContent()}
            </ModalBody>
        </Modal>
    );
};

const mapStateToProps = (state) => {
    return {
        userData: state.userData,
        loaderData: state.loaderData,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        showLoader: (loaderData) => dispatch(showLoader(loaderData)),
        hideLoader: (loaderData) => dispatch(hideLoader(loaderData)),
    };
};

// export default DockerTerminalModal;
export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(socketConnect(DockerTerminalModal));
